<script setup lang="ts">
  import { onBeforeMount, ref } from 'vue';
  import { useRoute } from 'vue-router';

  import { ComboSelect } from '@ui/components/combo-select';

  interface Props {
    iconName?: string;
    multiple?: boolean;
    optionLabel?: string;
    options: Record<string, any>[];
    optionValue?: string;
    placeholder?: string;
    queryName: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    iconName: 'bars-filter',
    multiple: false,
    optionLabel: 'label',
    optionValue: 'value',
    placeholder: 'Filter',
  });

  const emit = defineEmits<{
    (e: 'update', value: string[]): void;
  }>();

  const route = useRoute();
  const selectedOption = ref<Record<string, any>[]>([]);

  const updateSelection = (option) => {
    if (props.multiple) {
      return emit(
        'update',
        Array.isArray(option)
          ? option?.map((c) => c?.[props.optionValue])
          : option
            ? [option?.[props.optionValue]]
            : [],
      );
    }

    return emit('update', option?.[props.optionValue]);
  };

  onBeforeMount(() => {
    if (route.query?.[props.queryName]) {
      const routeFilterQuery = route.query[props.queryName];
      const routeFilterArray = Array.isArray(routeFilterQuery)
        ? routeFilterQuery
        : [routeFilterQuery];

      selectedOption.value = routeFilterArray
        .map((val) => {
          return props.options?.find((option) => option[props.optionValue] === val);
        })
        .filter((option): option is Record<string, any> => option !== undefined);
    }
  });

  defineExpose({
    selectedOption,
  });
</script>

<template>
  <ComboSelect
    v-if="options?.length"
    v-model="selectedOption"
    :options="options"
    searchable
    variant="filter"
    :placeholder="queryName"
    :multiple="multiple"
    :search-placeholder="`Filter by ${queryName}...`"
    :option-identifier="optionLabel"
    @update:model-value="(option) => updateSelection(option)"
  >
    <template #filter-label>
      <i class="fas text-xs" :class="`fa-${iconName}`" />
      <span> {{ placeholder }} </span>
    </template>
  </ComboSelect>
</template>
